import { Dayjs } from 'dayjs';
import { array, date, number, object, string } from 'yup';

import { ProviderName, TournamentRewardType } from 'v2/constants';
import { Nullable } from 'v2/types';
import { ImagesBreakPoints } from 'v2/types/ImagesBreakPoints';

export interface ITournamentForm {
  name: string;
  images: ImagesBreakPoints;
  endDate: Nullable<Dayjs>;
  startDate: Nullable<Dayjs>;
  providerNames: ProviderName[];
  balance: number;
  rewards: TournamentReward[];
  prizeFund: number;
  description: string;
  groupId?: string;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
}

export interface TournamentReward {
  type: TournamentRewardType;
  value: string;
  count: number;
}

export interface StreamerPrize {
  value: string;
}

export const TOURNAMENT_REWARD_STUB: TournamentReward = {
  type: TournamentRewardType.BALANCE,
  value: '',
  count: 1,
};

export const STREAMER_PRIZE: StreamerPrize = {
  value: '',
};

export const initialValues: ITournamentForm = {
  name: '',
  description: '',
  images: {
    image390Id: '',
    image768Id: null,
    image1024Id: '',
    image1300Id: null,
  },
  startDate: null,
  endDate: null,
  balance: 0,
  providerNames: [],
  rewards: [TOURNAMENT_REWARD_STUB],
  prizeFund: 0,
  groupId: '',
  isFirstInGroup: false,
  isLastInGroup: false,
};

export const validationSchema = object().shape({
  name: string().required().max(20, 'Must not contain more than 20 characters'),
  description: string().required(),
  images: object().shape({
    image390Id: string().required(),
    image768Id: string().nullable(),
    image1024Id: string().required(),
    image1300Id: string().nullable(),
  }),
  startDate: date().required(),
  endDate: date().required(),
  providerNames: array().of(string()).required(),
  balance: number().required(),
  prizeFund: number().required(),
  groupId: string().nullable(),
  rewards: array()
    .of(
      object().shape({
        type: string().oneOf(Object.values(TournamentRewardType), 'Invalid reward type').required('Type is required'),
        value: string()
          .required('Value is required')
          .when('type', {
            is: (type: TournamentRewardType) =>
              type === TournamentRewardType.BALANCE || type === TournamentRewardType.DEFAULT_CONTEST_TICKET,
            then: (schema) =>
              schema
                .matches(/^\d+(\.\d+)?$/, 'Must be a valid number')
                .test('positive', 'Must be a positive number', (v) => Number(v) > 0),
            otherwise: (schema) => schema.trim().min(1, 'Must be a non-empty string'),
          }),
        count: number().integer('Must be an integer').min(1, 'Count must be at least 1').required('Count is required'),
      })
    )
    .min(1, 'At least one reward is required'),
});
